/**
 * Current user's assignments (feeds myQuizzesStore). Mount ONCE, in AuthenticatedLayout.
 * The `assigneeEmail ==` filter is required: the security rules can only prove a list query
 * is the owner's when it carries that equality.
 */

import { useEffect } from 'react';
import { where } from 'firebase/firestore';
import { useFirebaseStore, useFirestoreCollection } from '@/shared';
import { getQuizAssignmentsCollectionPath } from '@/shared/config';
import type { QuizAssignment } from '../types';
import { parseAssignment } from '../utils/parse';
import { nextAvailabilityChange } from '../utils/assignmentStatus';
import { useMyQuizzesStore } from '../store/myQuizzesStore';

/** setTimeout overflows (fires immediately) past ~24.8 days, so long waits are re-armed hourly. */
const MAX_TIMER_MS = 60 * 60 * 1000;

export function useMyQuizAssignments(): void {
  const { db, isConnected, currentUser, visibilityRefreshKey } = useFirebaseStore();
  const email = currentUser?.email?.trim().toLowerCase() ?? null;
  const setAssignments = useMyQuizzesStore((s) => s.setAssignments);
  const setLoading = useMyQuizzesStore((s) => s.setLoading);
  const setError = useMyQuizzesStore((s) => s.setError);
  const setNow = useMyQuizzesStore((s) => s.setNow);
  const assignments = useMyQuizzesStore((s) => s.assignments);
  const now = useMyQuizzesStore((s) => s.now);

  // Reveal scheduled assignments while the app is open: wake at the next start date.
  // Each wake bumps `now`, which re-runs this effect and arms the next timer.
  useEffect(() => {
    const wait = nextAvailabilityChange(assignments, now);
    if (wait === null) return;
    const id = setTimeout(() => setNow(Date.now()), Math.min(Math.max(wait, 0) + 50, MAX_TIMER_MS));
    return () => clearTimeout(id);
  }, [assignments, now, setNow]);

  useFirestoreCollection<QuizAssignment>({
    db,
    collectionPath: email ? getQuizAssignmentsCollectionPath() : null,
    queryConstraints: email ? [where('assigneeEmail', '==', email)] : [],
    queryKey: email ?? '',
    parseDocument: parseAssignment,
    onDataLoaded: setAssignments,
    onLoadingChange: setLoading,
    onError: setError,
    enabled: isConnected && !!db && !!email,
    refreshKey: visibilityRefreshKey,
  });
}
